import * as React from 'react';

import {
  Button,
  Checkbox,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from '@rematter/ui-web';
import { Building2 } from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';

import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';

export type PlaidAccountInfo = {
  id: string;
  name: string;
  type: string;
  mask: string | null;
};

export type FacilityMapping = {
  plaidAccountId: string;
  facilityID: string;
};

type PlaidFacilityAssignmentDialogProps = {
  isOpen: boolean;
  accounts: PlaidAccountInfo[];
  onConfirm: (mappings: FacilityMapping[]) => void;
  onCancel: () => void;
  isLoading?: boolean;
};

export const PlaidFacilityAssignmentDialog = ({
  isOpen,
  accounts,
  onConfirm,
  onCancel,
  isLoading,
}: PlaidFacilityAssignmentDialogProps) => {
  const t = useTranslations();
  const [applyToAll, setApplyToAll] = React.useState(true);
  const [sharedFacilityId, setSharedFacilityId] = React.useState<
    string | undefined
  >(undefined);
  const [perAccountFacilities, setPerAccountFacilities] = React.useState<
    Record<string, string | undefined>
  >({});

  React.useEffect(() => {
    if (isOpen) {
      setApplyToAll(true);
      setSharedFacilityId(undefined);
      setPerAccountFacilities({});
    }
  }, [isOpen]);

  const handlePerAccountChange = (accountId: string, facilityId?: string) => {
    setPerAccountFacilities((prev) => ({
      ...prev,
      [accountId]: facilityId,
    }));
  };

  const handleConfirm = () => {
    const mappings: FacilityMapping[] = [];

    for (const account of accounts) {
      const facilityId = applyToAll
        ? sharedFacilityId
        : perAccountFacilities[account.id];

      if (facilityId) {
        mappings.push({
          plaidAccountId: account.id,
          facilityID: facilityId,
        });
      }
    }

    onConfirm(mappings);
  };

  const isValid = React.useMemo(() => {
    if (applyToAll) {
      return sharedFacilityId !== undefined;
    }

    return accounts.every(
      (account) => perAccountFacilities[account.id] !== undefined,
    );
  }, [applyToAll, sharedFacilityId, perAccountFacilities, accounts]);

  const formatAccountLabel = (account: PlaidAccountInfo) => {
    const mask = account.mask ? ` ••••${account.mask}` : '';
    return `${account.name} (${account.type})${mask}`;
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={(open) => !open && onCancel()}
      className="@PlaidFacilityAssignmentDialog"
    >
      <DialogContent position="center">
        <DialogHeader slot="title">
          <DialogTitle>
            {t.financialAccounts.plaid.facilityDialog.title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 p-4">
          <p className="text-sm text-neutral/70">
            {t.financialAccounts.plaid.facilityDialog.description}
          </p>

          {accounts.length > 1 && (
            <Checkbox isSelected={applyToAll} onChange={setApplyToAll}>
              {t.financialAccounts.plaid.facilityDialog.applyToAll}
            </Checkbox>
          )}

          {applyToAll ? (
            <div className="flex flex-col gap-3">
              <SelectFacility
                label={t.financialAccounts.plaid.facilityDialog.facilityLabel}
                value={sharedFacilityId}
                onChange={setSharedFacilityId}
                isRequired
              />

              <div className="rounded-md border border-neutral/20 bg-neutral/5 p-3">
                <p className="mb-2 text-sm font-medium text-neutral/70">
                  {t.financialAccounts.plaid.facilityDialog.accountsToConnect}
                </p>
                <ul className="flex flex-col gap-1">
                  {accounts.map((account) => (
                    <li
                      key={account.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Building2 className="size-4 text-neutral/50" />
                      <span>{formatAccountLabel(account)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {accounts.map((account) => (
                <div key={account.id} className="flex flex-col gap-2">
                  <p className="text-sm font-medium">
                    {formatAccountLabel(account)}
                  </p>
                  <SelectFacility
                    label={
                      t.financialAccounts.plaid.facilityDialog.facilityLabel
                    }
                    value={perAccountFacilities[account.id]}
                    onChange={(value) =>
                      handlePerAccountChange(account.id, value)
                    }
                    isRequired
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex-none border-t border-neutral/20 bg-neutral/5 p-4">
          <div className="flex justify-end gap-3">
            <Button onPress={onCancel} isDisabled={isLoading}>
              {t.financialAccounts.plaid.facilityDialog.cancel}
            </Button>
            <Button
              onPress={handleConfirm}
              color="primary"
              isDisabled={!isValid}
              isLoading={isLoading}
            >
              {t.financialAccounts.plaid.facilityDialog.connect}
            </Button>
          </div>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
